/* global Sys, Core */
Sys.ns("Core");

/**
 * The base Model class.
 *
 * This class should be used when creating a new model.
 *
 * The purpose of the model is to hold information needed by the entire module and also keep track of the
 * internal state of the module.
 *
 * @class Core.Model
 * @extends Sys.Observable
 * @aside guide events__events
 */
Core.Model = {

    /**
     * @cfg name The name of the module
     */

    /**
     * @constructs
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    constructor: function(config) {
        Core.Model.superclass.constructor.apply(this, arguments);

        this.init(config);
    },

    /**
     * Initializes the model by creating the data storage.
     *
     * @deprecated 8.0.0. Override Core.Model#setupData instead as this method will be removed and moved to the constructor.
     * @protected
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    init: function(config) {
        this.data = {};
        this.state = undefined;
        this.MODULE_NAME = config && config.name ? config.name : null;

        this.setupData();
    },

    /**
     * This method should setup the initial data for the module.
     *
     * E.g. hard coded values, initial server response information or
     * values read from the config file.
     *
     * @example
     * An example that stores some reel information from the config:
     *
     *     var me = this,
     *         reelConfig = Game.stage.model.getCurrentReelGroupConfiguration();
     *
     *     me.storeData("numReels", reelConfig.symbolCols);
     *     me.storeData("numSymbols", reelConfig.symbolRows);
     *
     * @protected
     * @returns {void}
     */
    setupData: function() {
        // Intentionally left empty
    },

    /**
     * Returns the data associated with the given key.
     *
     * @example
     *     this.readData("foo"); // => undefined
     *
     *     this.storeData("foo", "bar");
     *     this.readData("foo"); // => "bar"
     *
     * @param {string} key The data storage key.
     * @returns {Mixed} The data read from storage.
     */
    readData: function(key) {
        return this.data[key];
    },

    /**
     * Store the given data under the given key.
     *
     * @example
     *     this.storeData("foo", "bar"); // => "bar"
     *     this.readData("foo");         // => "bar"
     *
     * @param {string} key The name of the key under which to store the data.
     * @param {*} dataToStore The data to store.
     * @returns {*} The data that was stored.
     */
    storeData: function(key, dataToStore) {
        this.data[key] = dataToStore;
        return this.data[key];
    },

    /**
     * Removes the data associated with the given key.
     *
     * Returns `undefined` on next read.
     *
     * @example
     *     this.storeData("foo", "bar");
     *     this.readData("foo");   // => "bar"
     *     this.removeData("foo"); // => undefined
     *     this.readData("foo");   // => undefined
     *
     * @param {string} key The data storage key.
     * @returns {void}
     */
    removeData: function(key) {
        delete this.data[key];
    },

    /**
     * Sets the given state and signals that the model now is in that state.
     *
     * @deprecated 8.0.0. Store data in model instead.
     * @param {string} newState The new state.
     * @param {Object} [param=undefined] Optional parameters to be passed to the event.
     * @returns {void}
     */
    setState: function(newState, param) {
        this.state = newState;
        this.fireEvent("model:" + this.state, param);
    },

    /**
     * Returns the current state.
     *
     * @deprecated 8.0.0. Retrieve data from model instead.
     * @returns {string} The current state.
     */
    getState: function() {
        return this.state;
    },

    /**
     * Checks if the models current state is the same as the given state.
     *
     * @deprecated 8.0.0. Retrieve data from model instead.
     * @param {*} state State.
     * @returns {boolean} If state matches provided.
     */
    isState: function(state) {
        return state === this.state;
    }
};

Core.Model = Sys.extend(Sys.Observable, Core.Model, "Core.Model");
